"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import {
  ArrowRight,
  PlayCircle,
  VolumeX,
  Volume2,
  Play,
  Pause,
  Maximize2,
  Headphones,
} from "lucide-react";
import { business } from "@/content/business";
import { heroVideo } from "@/content/videos";

export function HeroSection() {
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [teaserIndex, setTeaserIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const wasPlayingOnExitRef = useRef(false);

  const teasers = [
    '"Механизированная штукатурка — это скорость и качество..."',
    '"Мы используем только профессиональное оборудование..."',
    '"Личная гарантия качества от руководителя..."',
    '"Честность, качество, скорость — наш девиз..."',
    '"Более 1000 проектов по всей Чеченской Республике..."',
  ];

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");

    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMobile(mediaQuery.matches);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (isMuted && !isMobile) {
      const interval = setInterval(() => {
        setTeaserIndex((prev) => (prev + 1) % teasers.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isMuted, isMobile]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleLoadedMetadata = () => setDuration(video.duration);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, []);

  // Pause video when scrolling out of view
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && isPlaying) {
            wasPlayingOnExitRef.current = true;
            video.pause();
          } else if (entry.isIntersecting && wasPlayingOnExitRef.current) {
            wasPlayingOnExitRef.current = false;
            void video.play();
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      },
    );

    const heroSection = document.getElementById("hero-section");
    if (heroSection) {
      observer.observe(heroSection);
    }

    return () => {
      if (heroSection) {
        observer.unobserve(heroSection);
      }
    };
  }, [isPlaying]);

  const toggleAudio = () => {
    const video = videoRef.current;
    if (!video) return;

    const nextMutedState = !isMuted;
    video.muted = nextMutedState;

    if (!isMobile && video.paused && !nextMutedState) {
      void video.play();
    }

    setIsMuted(nextMutedState);
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const toggleFullscreen = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      // iOS Safari uses a different method
      if ("webkitEnterFullscreen" in video) {
        // iOS Safari specific
        // @ts-expect-error because web
        (video as unknown).webkitEnterFullscreen();

        // Unmute and play when entering fullscreen
        if (isMuted) {
          video.muted = false;
          setIsMuted(false);
        }
        if (video.paused) {
          await video.play();
        }
        return;
      }

      // Standard Fullscreen API with vendor prefixes
      const requestFullscreen =
        video.requestFullscreen ||
        // @ts-expect-error because error
        (video as unknown).webkitRequestFullscreen ||
        // @ts-expect-error because error
        (video as unknown).mozRequestFullScreen ||
        // @ts-expect-error because error
        (video as unknown).msRequestFullscreen;

      if (requestFullscreen) {
        await requestFullscreen.call(video);

        // Unmute and play when entering fullscreen
        if (isMuted) {
          video.muted = false;
          setIsMuted(false);
        }
        if (video.paused) {
          await video.play();
        }
      } else {
        // Fallback: Show native controls for better mobile experience
        video.controls = true;
        video.muted = false;
        setIsMuted(false);
        await video.play();
      }
    } catch (error) {
      console.error("Fullscreen error:", error);

      // Fallback: Enable native controls
      video.controls = true;
      if (isMuted) {
        video.muted = false;
        setIsMuted(false);
      }
      if (video.paused) {
        video.play().catch((e) => console.error("Play error:", e));
      }
    }
  };

  // Handle fullscreen state changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleFullscreenChange = () => {
      const isFullscreen =
        document.fullscreenElement === video ||
        // @ts-expect-error because error
        (document as unknown).webkitFullscreenElement === video ||
        // @ts-expect-error because error
        (document as unknown).mozFullScreenElement === video ||
        // @ts-expect-error because error
        (document as unknown).msFullscreenElement === video ||
        // @ts-expect-error because error
        (video as unknown).webkitDisplayingFullscreen;

      // When exiting fullscreen, hide native controls if they were shown
      if (!isFullscreen && video.controls) {
        video.controls = false;
      }
    };

    // Listen for fullscreen changes
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );
    };
  }, []);

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = pos * duration;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <section
      id="hero-section"
      className="bg-luxury-dark relative flex min-h-screen flex-col overflow-hidden"
      data-audio-state={isMuted ? "muted" : "unmuted"}
    >
      {/* ZONE 1: Video Hero */}
      <div className="relative h-[60vh] w-full sm:h-[70vh] lg:flex lg:min-h-screen lg:items-center">
        {/* Video Background */}
        <div
          id="hero-video-container"
          className="absolute inset-0 h-full w-full"
          data-audio-state={isMuted ? "muted" : "unmuted"}
        >
          <video
            ref={videoRef}
            id="hero-promo-video"
            className="h-full w-full object-cover"
            playsInline
            muted
            loop
            autoPlay
            preload="metadata"
            poster={heroVideo.poster}
            aria-label="Промо-видео Флагман Строй"
          >
            <source src={heroVideo.src} type="video/mp4" />
          </video>

          {/* Gradient Overlays */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-transparent lg:to-black/80" />
          <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-r from-black/40 via-transparent to-black/20 lg:block" />

          {/* Mobile: Bottom Fade to Content */}
          <div className="to-luxury-dark pointer-events-none absolute right-0 bottom-0 left-0 h-32 bg-gradient-to-b from-transparent lg:hidden" />
        </div>

        {/* Minimal Video Overlay (Mobile: Top corner only) */}
        <div className="absolute top-6 right-6 left-6 z-20 flex items-start justify-between lg:hidden">
          <div className="glass flex items-center space-x-2 rounded-full px-4 py-2">
            <div className="bg-luxury-green h-2 w-2 animate-pulse rounded-full" />
            <span className="text-xs font-semibold tracking-wider text-white/90 uppercase">
              В эфире
            </span>
          </div>
        </div>

        {/* Desktop Content Overlay */}
        <div className="relative z-20 mx-auto hidden w-full max-w-7xl px-6 lg:block lg:px-12">
          <div className="max-w-4xl">
            <div className="text-luxury-green animate-fade-in mb-8 inline-flex items-center space-x-3">
              <div className="bg-luxury-green h-0.5 w-12" />
              <span className="text-sm font-semibold tracking-[0.3em] text-white/90 uppercase">
                С {business.foundedYear} года в Чечне
              </span>
            </div>

            <h1 className="font-display animate-slide-up mb-6 text-5xl leading-[0.95] font-bold text-white sm:text-6xl lg:text-8xl">
              Качество
              <br />
              <span className="text-luxury-green italic">проверенное</span>
              <br />
              временем
            </h1>

            <p className="animate-slide-up mb-8 max-w-2xl text-lg leading-relaxed text-white/80 lg:text-xl">
              Механизированная штукатурка и полусухая стяжка по всей Чеченской
              Республике. Более {business.projectsCount} завершённых проектов.
            </p>

            {isMuted && (
              <div className="animate-slide-up mb-8 min-h-[60px]">
                <p className="text-base leading-relaxed text-white/70 italic transition-opacity duration-500">
                  {teasers[teaserIndex]}
                </p>
              </div>
            )}

            <div className="animate-slide-up mb-12 flex flex-wrap gap-4">
              <Link
                href="#contact"
                className="group bg-luxury-green text-luxury-dark hover-lift inline-flex items-center space-x-3 px-8 py-4 font-bold tracking-wider transition-all duration-300"
              >
                <span>БЕСПЛАТНЫЙ ЗАМЕР</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-2" />
              </Link>
              <Link
                href="#portfolio"
                className="glass inline-flex items-center space-x-3 px-8 py-4 font-semibold tracking-wider text-white transition-all duration-300 hover:bg-white/20"
              >
                <PlayCircle className="h-5 w-5" />
                <span>НАШИ РАБОТЫ</span>
              </Link>
            </div>

            <div className="animate-fade-in flex items-center space-x-8 border-t border-white/20 pt-8">
              <div>
                <p className="font-display text-luxury-green text-3xl font-bold">
                  {business.projectsCount}+
                </p>
                <p className="text-sm text-white/70">Проектов</p>
              </div>
              <div>
                <p className="font-display text-luxury-green text-3xl font-bold">
                  0
                </p>
                <p className="text-sm text-white/70">Недовольных</p>
              </div>
              <div>
                <p className="font-display text-luxury-green text-3xl font-bold">
                  ЧР
                </p>
                <p className="text-sm text-white/70">По всей республике</p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile: Dynamic Audio Toggle */}
        <button
          onClick={toggleAudio}
          className="absolute z-30 flex items-center justify-center rounded-full border-2 border-white/30 bg-black/60 backdrop-blur-md transition-all duration-500 ease-out active:scale-95 lg:hidden"
          style={{
            top: isMuted ? "50%" : "auto",
            left: isMuted ? "50%" : "auto",
            bottom: isMuted ? "auto" : "6.5rem",
            right: isMuted ? "auto" : "1.5rem",
            transform: isMuted ? "translate(-50%, -50%)" : "translate(0, 0)",
            width: isMuted ? "80px" : "48px",
            height: isMuted ? "80px" : "48px",
          }}
          aria-label="Включить/выключить звук"
        >
          <div className="relative">
            {isMuted ? (
              <VolumeX className="h-10 w-10 text-white transition-all duration-300" />
            ) : (
              <Volume2 className="h-6 w-6 text-white transition-all duration-300" />
            )}
            {isMuted && (
              <div className="bg-luxury-green absolute -top-1 -right-1 h-4 w-4 rounded-full">
                <div className="bg-luxury-green absolute inset-0 animate-ping rounded-full" />
              </div>
            )}
          </div>
        </button>

        {/* Mobile: Control Bar */}
        {!isMuted && (
          <div className="pointer-events-auto absolute right-6 bottom-6 left-6 z-30 opacity-100 transition-all duration-500 ease-out lg:hidden">
            <div className="glass flex items-center justify-between rounded-full px-4 py-3">
              <button
                onClick={togglePlay}
                className="flex items-center space-x-2 rounded-full px-3 py-2 transition-all duration-200 hover:bg-white/10 active:scale-95"
                aria-label="Воспроизвести/пауза"
              >
                {isPlaying ? (
                  <Pause className="h-5 w-5 text-white" />
                ) : (
                  <Play className="h-5 w-5 text-white" />
                )}
                <span className="text-sm font-semibold text-white">
                  {isPlaying ? "Пауза" : "Играть"}
                </span>
              </button>

              <button
                onClick={toggleFullscreen}
                className="bg-luxury-green/20 hover:bg-luxury-green/30 flex items-center space-x-2 rounded-full px-4 py-2 transition-all duration-200 active:scale-95"
                aria-label="Развернуть видео"
              >
                <Maximize2 className="h-5 w-5 text-white" />
                <span className="text-sm font-semibold text-white">
                  Развернуть
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Desktop Controls */}
        <div className="absolute bottom-8 left-1/2 z-30 hidden -translate-x-1/2 transition-opacity duration-500 lg:flex">
          <div className="glass flex items-center space-x-6 rounded-full px-6 py-4">
            <button
              onClick={togglePlay}
              className="bg-luxury-green/20 hover:bg-luxury-green/30 group flex h-12 w-12 items-center justify-center rounded-full transition-all duration-300"
              aria-label="Воспроизвести/Пауза"
            >
              {isPlaying ? (
                <Pause className="h-6 w-6 text-white" />
              ) : (
                <Play className="h-6 w-6 text-white" />
              )}
            </button>

            <div className="flex items-center space-x-3">
              <span className="min-w-[40px] font-mono text-sm text-white/80">
                {formatTime(currentTime)}
              </span>
              <div
                onClick={handleProgressClick}
                className="h-1 w-32 cursor-pointer overflow-hidden rounded-full bg-white/20 lg:w-48"
              >
                <div
                  className="bg-luxury-green h-full rounded-full transition-all duration-300"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
              </div>
              <span className="min-w-[40px] font-mono text-sm text-white/80">
                {formatTime(duration)}
              </span>
            </div>

            <button
              onClick={toggleAudio}
              className="group relative"
              aria-label="Включить/выключить звук"
            >
              <div className="bg-luxury-green/20 hover:bg-luxury-green/30 flex items-center space-x-3 rounded-full px-4 py-2 transition-all duration-300">
                {isMuted ? (
                  <VolumeX className="h-5 w-5 text-white" />
                ) : (
                  <Volume2 className="h-5 w-5 text-white" />
                )}
                <span className="text-sm font-semibold text-white">Звук</span>
              </div>
              {isMuted && (
                <div className="bg-luxury-green absolute -top-2 -right-2 h-3 w-3 animate-ping rounded-full" />
              )}
            </button>

            <button
              onClick={toggleFullscreen}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 transition-all duration-300 hover:bg-white/20"
              aria-label="Полноэкранный режим"
            >
              <Maximize2 className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Desktop Scroll Indicator */}
        <div className="absolute right-8 bottom-8 z-20 hidden animate-bounce lg:block">
          <div className="flex flex-col items-center space-y-2">
            <span className="origin-center -rotate-90 transform text-xs tracking-widest text-white/60 uppercase">
              Прокрутите
            </span>
          </div>
        </div>
      </div>

      {/* ZONE 2: Content Panel (Mobile) */}
      <div className="bg-luxury-dark relative z-20 lg:hidden">
        <div className="space-y-6 px-6 py-10">
          <div className="text-luxury-green inline-flex items-center space-x-2">
            <div className="bg-luxury-green h-0.5 w-8" />
            <span className="text-xs font-semibold tracking-[0.25em] text-white/90 uppercase">
              С {business.foundedYear} года
            </span>
          </div>

          <h1 className="font-display text-4xl leading-[0.95] font-bold text-white sm:text-5xl">
            Качество
            <br />
            <span className="text-luxury-green italic">проверенное</span>
            <br />
            временем
          </h1>

          <p className="text-base leading-relaxed text-white/75">
            Механизированная штукатурка и полусухая стяжка по всей Чеченской
            Республике
          </p>

          <Link
            href="#contact"
            className="group bg-luxury-green text-luxury-dark inline-flex w-full items-center justify-center space-x-3 px-6 py-4 text-center font-bold tracking-wider transition-all duration-300 active:scale-95"
          >
            <span>БЕСПЛАТНЫЙ ЗАМЕР</span>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>

          {isMuted && (
            <div className="bg-luxury-green/10 border-luxury-green/30 flex items-center space-x-3 rounded-lg border px-4 py-3 transition-all duration-300">
              <Headphones className="text-luxury-green h-5 w-5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">
                  Включите звук
                </p>
                <p className="text-xs text-white/60">Услышьте нашу историю</p>
              </div>
              <button
                onClick={toggleAudio}
                className="bg-luxury-green text-luxury-dark rounded-full px-4 py-2 text-xs font-bold transition-all duration-200 active:scale-95"
              >
                ВКЛ
              </button>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 border-t border-white/10 pt-6">
            <div className="text-center">
              <p className="font-display text-luxury-green text-2xl font-bold">
                {business.projectsCount}+
              </p>
              <p className="mt-1 text-xs text-white/60">Проектов</p>
            </div>
            <div className="text-center">
              <p className="font-display text-luxury-green text-2xl font-bold">
                0
              </p>
              <p className="mt-1 text-xs text-white/60">Недовольных</p>
            </div>
            <div className="text-center">
              <p className="font-display text-luxury-green text-2xl font-bold">
                ЧР
              </p>
              <p className="mt-1 text-xs text-white/60">Вся республика</p>
            </div>
          </div>

          <Link
            href="#portfolio"
            className="inline-flex w-full items-center justify-center space-x-2 border-2 border-white/20 px-6 py-3 text-center font-semibold tracking-wide text-white transition-all duration-300 active:bg-white/10"
          >
            <PlayCircle className="h-5 w-5" />
            <span>НАШИ РАБОТЫ</span>
          </Link>
        </div>
      </div>
    </section>
  );
}
